import { useDisclosure } from '@chakra-ui/react';
import { Drawer, RebusHero } from '@time-line/components/common';
import type { SanityImage } from '@time-line/components/types';
import { PageModuleResolver } from 'components/common/PageModuleResolver';
import { PageWrapper } from 'components/common/PageWrapper';
import {
  MixedContentContainer,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { Seo } from 'components/common/Seo';
import { PromotionHero } from 'components/heros/PromotionHero';
import { useRatingsOverview } from 'hooks/reviews/useRatingsOverview';
import type { PromotionPage } from 'types/cms/pages/PromotionPage';
import { addReviewsToOverline } from 'utils/addReviewsToOverline';

interface PromoPageContentProps extends PromotionPage {
  noindex?: boolean;
}

export function PromoPageContent({
  title,
  heroType = 'promotion',
  hero,
  rebusHero,
  termsAndConditions,
  blocks,
  seo,
  noindex = true,
}: PromoPageContentProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: ratingsOverview } = useRatingsOverview();

  const heroOverline = hero?.overline
    ? addReviewsToOverline(hero.overline, ratingsOverview)
    : undefined;

  const anchorBlock = blocks?.find(
    (block) =>
      block._type === 'timeline_productBlock' ||
      block._type === 'productBundle' ||
      block._type === 'timeline_supplementPicker' ||
      block._type === 'timeline_skincareFormatPicker' ||
      block._type === 'timeline_signupBanner',
  );

  return (
    <PageWrapper>
      <Seo
        noindex={noindex}
        seo={{
          title: seo?.title ?? title,
          description: seo?.description ?? hero?.text,
          image:
            seo?.image ??
            (hero?.media?._type === 'background'
              ? ({
                  ...hero.media.image,
                  _type: 'image',
                } as SanityImage)
              : undefined),
        }}
      />

      {heroType === 'rebus' && rebusHero && <RebusHero hero={rebusHero} />}

      {heroType === 'promotion' && (
        <PromotionHero
          {...hero}
          overline={heroOverline}
          title={title}
          onButtonClick={onOpen}
          hasTerms={!!termsAndConditions}
          anchorBlock={anchorBlock}
        />
      )}

      {!!termsAndConditions && (
        <Drawer
          isOpen={isOpen}
          onClose={onClose}
          title="Terms & Conditions"
          maxWidth="lg"
        >
          <RichTextResolver
            container={MixedContentContainer}
            blocks={termsAndConditions}
          />
        </Drawer>
      )}
      {blocks && <PageModuleResolver blocks={blocks} />}
    </PageWrapper>
  );
}
